<template>
    <DropdownField v-if="authStore.currentConta?.idUsuarioAxion"
        :distance="-4"
        placement="right-start"
        :triggers="['click']"
        strategy="fixed"
        :delay="{ show: 200, hide: 100 }"
        interactive
    >
        <template #default>
            <button type="button" class="dropdown-item-v submenu-trigger">
                <span>Ações AXION</span>
                <i class="ri-arrow-right-s-line"></i>
            </button>
        </template>
        <template #popper>
            <div class="dropdown-menu-v axion-menu">
                <template v-if="axionAtiva">
                    <button v-if="contatoVinculado" type="button" class="dropdown-item-v" @click="acessarFicha">
                        <span>Acessar Ficha</span>
                    </button>
                    <button v-else type="button" class="dropdown-item-v" @click="openVincularAxion">
                        <span>Vincular ao AXION</span><i class="ri-links-line ms-2 text-warning"></i>
                    </button>
                    <button v-if="contatoVinculado && temModuloChamado" type="button" class="dropdown-item-v" @click="openAbrirChamado">
                        <span>Abrir Chamado</span>
                    </button>
                    <button v-if="contatoVinculado && temModuloChamado" type="button" class="dropdown-item-v" @click="openChamadosContato">
                        <span>Chamados do contato</span>
                    </button>
                    <button v-if="contatoVinculado" type="button" class="dropdown-item-v" @click="openDispararProcesso">
                        <span>Disparar Processo</span>
                    </button>
                    <button v-if="contatoVinculado" type="button" class="dropdown-item-v" @click="openProcessosContato">
                        <span>Processos do contato</span>
                    </button>
                    <button type="button" class="dropdown-item-v" @click="gerarTarefa">
                        <span>Gerar Tarefa</span>
                    </button>
                    <button type="button" class="dropdown-item-v" @click="openGerarOportunidade">
                        <span>Gerar Oportunidade</span>
                    </button>
                    <button v-if="podeGerarSalaReuniao" type="button" class="dropdown-item-v" @click="gerarSalaReuniao">
                        <span>Gerar Sala de Reunião</span>
                    </button>
                    <button type="button" class="dropdown-item-v" @click="openOportunidadesAxion">
                        <span>Oportunidades AXION</span>
                    </button>
                    <button v-if="contatoVinculado && authStore.user?.hasGlobalAccess" type="button" class="dropdown-item-v" @click="desvincularResponsavel">
                        <span>Desvincular do AXION</span><i class="ri-link-unlink-m ms-2 text-danger"></i>
                    </button>
                </template>

                <template v-else>
                    <div class="grpro-ad-block">
                        <small>Impulsione seu {{ branding.companyName }} ainda mais com o <strong>GRPRO</strong></small>
                        <a href="https://grpro.app.br/assistir-demonstracao/" target="_blank" class="btn btn-info btn-sm mt-2 w-100">
                            Conheça o GRPRO
                        </a>
                    </div>
                </template>
            </div>
        </template>
    </DropdownField>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { branding } from '@/config/branding';
import { hideAllPoppers } from 'floating-vue';
import { useUiStore } from '@/stores/app/uiStore';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { useChatStore } from '@/stores/app/chatStore';
import { useIntegracaoAxionStore } from '@/stores/app/integracaoAxionStore';
import { montarLinkSalaReuniao } from '@/utils/sala-reuniao-axion';

const uiStore = useUiStore();
const authStore = useAuthStoreUser();
const chatStore = useChatStore();
const axionStore = useIntegracaoAxionStore();

const axionAtiva = computed(() => !!authStore.currentConta?.integracaoAxionAtiva);
const contatoVinculado = computed(() => !!chatStore.activeChat?.idClienteAxion);
const temModuloChamado = computed(() => {
    const idContato = chatStore.activeChat?.idContato;
    return !!idContato && !!axionStore.temModuloChamadoByContato[idContato];
});
// A sala é montada no front a partir do número do contato + inbox, então só precisa
// da URL base do AXION. O vínculo do atendente com o AXION já é exigido pelo v-if do
// dropdown (currentConta.idUsuarioAxion).
const podeGerarSalaReuniao = computed(() =>
    !!authStore.currentConta?.urlAxion
    && !!chatStore.activeChat?.codigoContato
    && !!chatStore.activeChat?.idInbox
);

// O licenciado pode não ter o módulo de chamados — nesse caso a ação não aparece
// mesmo com o contato vinculado. Verificamos ao vivo quando o contato muda.
watch(
    () => axionAtiva.value && contatoVinculado.value ? chatStore.activeChat?.idContato : undefined,
    (idContato) => {
        if (idContato && axionStore.temModuloChamadoByContato[idContato] === undefined) {
            void axionStore.fetchTemModuloChamado(idContato);
        }
    },
    { immediate: true },
);

function openChamadosContato() {
    hideAllPoppers();
    uiStore.openChamadosContatoAxionModal();
}

function openDispararProcesso() {
    hideAllPoppers();
    uiStore.openDispararProcessoAxionModal();
}

function openProcessosContato() {
    hideAllPoppers();
    uiStore.openProcessosContatoAxionModal();
}

function gerarSalaReuniao() {
    hideAllPoppers();

    const chat = chatStore.activeChat;
    const urlAxion = authStore.currentConta?.urlAxion;
    if (!chat?.codigoContato || !chat.idInbox || !urlAxion) return;

    chatStore.inserirTextoNoInput(montarLinkSalaReuniao(urlAxion, chat.codigoContato, chat.idInbox));
}

function openGerarOportunidade() {
    hideAllPoppers();
    uiStore.openGerarOportunidadeModal();
}

function openOportunidadesAxion() {
    hideAllPoppers();
    uiStore.openOportunidadesAxionModal();
}

function openVincularAxion() {
    hideAllPoppers();
    uiStore.openVincularAxionModal();
}

function openAbrirChamado() {
    hideAllPoppers();
    uiStore.openAbrirChamadoAxionModal();
}

async function gerarTarefa() {
    hideAllPoppers();
    const idContato = chatStore.activeChat?.idContato;
    const idAtendimento = chatStore.activeChat?.id;
    if (!idContato || !idAtendimento) return;
    await axionStore.gerarTarefa(idContato, idAtendimento);
}

async function acessarFicha() {
    hideAllPoppers();
    const idContato = chatStore.activeChat?.idContato;
    if (!idContato) return;
    await axionStore.acessarFicha(idContato);
}

async function desvincularResponsavel() {
    hideAllPoppers();
    const idContato = chatStore.activeChat?.idContato;
    if (!idContato) return;
    await axionStore.desvincularResponsavel(idContato);
}
</script>

<style scoped>
.submenu-trigger { display: flex !important; justify-content: space-between !important; align-items: center !important; }
</style>
